<template>
  <div>
    <!-- Configuration card -->
    <div v-if="phase !== 'complete'" class="card border shadow-sm mb-4">
      <div class="card-header bg-white border-bottom">
        <h5 class="fw-bold mb-0 text-primary">
          <i class="fas fa-cloud-download-alt me-2"></i>Import Configuration
        </h5>
      </div>
      <div class="card-body">
        <div class="mb-3" style="max-width: 200px;">
          <label for="lookback-days" class="form-label fw-semibold">Lookback Days</label>
          <input
            id="lookback-days"
            v-model.number="lookback_days"
            type="number"
            class="form-control"
            min="1"
            max="365"
            :disabled="phase === 'previewing' || phase === 'preview_ready' || phase === 'importing'"
          />
        </div>

        <button
          v-if="phase === 'idle' || phase === 'previewing'"
          class="btn btn-secondary"
          :disabled="phase === 'previewing'"
          @click="previewImport"
        >
          <span v-if="phase === 'previewing'" class="spinner-border spinner-border-sm me-2"></span>
          <i v-else class="fas fa-search me-2"></i>Preview Import
        </button>
      </div>
    </div>

    <!-- Dry-run results card -->
    <div v-if="phase === 'preview_ready' || phase === 'importing'" class="card border border-warning shadow-sm mb-4">
      <div class="card-header bg-warning bg-opacity-10 border-bottom border-warning">
        <h5 class="fw-bold mb-0 text-warning">
          <i class="fas fa-eye me-2"></i>Preview — No changes have been made
        </h5>
      </div>
      <div class="card-body">
        <dl class="row mb-4">
          <dt class="col-sm-5">Total Fetched</dt>
          <dd class="col-sm-7">{{ previewResult.total_fetched }}</dd>

          <dt class="col-sm-5">Would Insert</dt>
          <dd class="col-sm-7">{{ previewResult.inserted }}</dd>

          <dt class="col-sm-5">Skipped (Not Filled)</dt>
          <dd class="col-sm-7">{{ previewResult.skipped_not_filled }}</dd>

          <dt class="col-sm-5">Skipped (Already Exists)</dt>
          <dd class="col-sm-7">{{ previewResult.skipped_already_exists }}</dd>

          <dt class="col-sm-5">Errors</dt>
          <dd class="col-sm-7">{{ previewResult.errors }}</dd>
        </dl>

        <div class="d-flex gap-2">
          <button
            class="btn btn-warning"
            :disabled="phase === 'importing'"
            @click="confirmImport"
          >
            <span v-if="phase === 'importing'" class="spinner-border spinner-border-sm me-2"></span>
            <i v-else class="fas fa-cloud-download-alt me-2"></i>Confirm Import
          </button>
          <button class="btn btn-secondary" :disabled="phase === 'importing'" @click="cancel">
            Cancel
          </button>
        </div>
      </div>
    </div>

    <!-- Success card -->
    <div v-if="phase === 'complete'" class="card border border-success shadow-sm mb-4">
      <div class="card-header bg-success bg-opacity-10 border-bottom border-success">
        <h5 class="fw-bold mb-0 text-success">
          <i class="fas fa-check-circle me-2"></i>Import Complete
        </h5>
      </div>
      <div class="card-body">
        <dl class="row mb-4">
          <dt class="col-sm-5">Total Fetched</dt>
          <dd class="col-sm-7">{{ importResult.total_fetched }}</dd>

          <dt class="col-sm-5">Inserted</dt>
          <dd class="col-sm-7">{{ importResult.inserted }}</dd>

          <dt class="col-sm-5">Skipped (Not Filled)</dt>
          <dd class="col-sm-7">{{ importResult.skipped_not_filled }}</dd>

          <dt class="col-sm-5">Skipped (Already Exists)</dt>
          <dd class="col-sm-7">{{ importResult.skipped_already_exists }}</dd>

          <dt class="col-sm-5">Errors</dt>
          <dd class="col-sm-7">{{ importResult.errors }}</dd>
        </dl>

        <button class="btn btn-outline-secondary" @click="reset">
          <i class="fas fa-redo me-2"></i>Run Another Import
        </button>
      </div>
    </div>

    <!-- Error display -->
    <div v-if="error" class="alert alert-danger d-flex justify-content-between align-items-start">
      <span><i class="fas fa-exclamation-circle me-2"></i>{{ error.detail || error.message }}</span>
      <button type="button" class="btn-close ms-3 flex-shrink-0" @click="error = null"></button>
    </div>
  </div>
</template>

<script>
import { importTradeHistory } from '../services/alpacaImportApi.js';

export default {
  name: 'AlpacaImportPanel',

  data() {
    return {
      phase: 'idle',
      lookback_days: 90,
      previewResult: null,
      importResult: null,
      error: null,
    };
  },

  methods: {
    async previewImport() {
      this.phase = 'previewing';
      this.error = null;
      try {
        console.log('[AlpacaImportPanel] Running dry-run preview.');
        const data = await importTradeHistory({ lookback_days: this.lookback_days, dry_run: true });
        this.previewResult = data;
        this.phase = 'preview_ready';
        console.log('[AlpacaImportPanel] Dry-run complete.', data);
      } catch (err) {
        this.error = err;
        this.phase = 'idle';
        console.error('[AlpacaImportPanel] Dry-run failed:', err);
      }
    },

    async confirmImport() {
      this.phase = 'importing';
      this.error = null;
      try {
        console.log('[AlpacaImportPanel] Running live import.');
        const data = await importTradeHistory({ lookback_days: this.lookback_days, dry_run: false });
        this.importResult = data;
        this.phase = 'complete';
        console.log('[AlpacaImportPanel] Live import complete.', data);
      } catch (err) {
        this.error = err;
        this.phase = 'preview_ready';
        console.error('[AlpacaImportPanel] Live import failed:', err);
      }
    },

    cancel() {
      this.previewResult = null;
      this.error = null;
      this.phase = 'idle';
    },

    reset() {
      this.previewResult = null;
      this.importResult = null;
      this.error = null;
      this.lookback_days = 90;
      this.phase = 'idle';
    },
  },
};
</script>
